<template>
  <div class="search-input-container">
    <v-text-field
      v-model="displayValue"
      :hide-details="hideDetails"
      :placeholder="placeholder"
      :rules="rules"
      :label="label"
      @input="handleSearchInput"
    >
      <template #prepend-inner>
        <v-avatar v-if="selectedCause" size="24" class="mr-2">
          <v-img
            v-if="selectedCause.logoUrl && selectedCause.logoUrl.length > 0"
            :src="selectedCause.logoUrl"
            :alt="
              t('donationDetails.form.donationCauseId.autoComplete.logoAltText', {
                causeName: selectedCause.name,
              })
            "
          />
          <v-icon v-else size="24">mdi-charity</v-icon>
        </v-avatar>
      </template>
      <template #append-inner>
        <v-btn v-if="selectedCause" icon variant="text" size="small" @click="clearSelectedCause">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-btn
          v-else-if="query && query.length > 0"
          icon
          variant="text"
          size="small"
          @click="clearSearch"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-text-field>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { ENDPOINTS, get, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { getUserCountry } from '@/utils/userCountry';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  props: {
    hideDetails: { type: Boolean, default: true },
    placeholder: { type: String, default: '' },
    rules: { type: Array, default: () => [] },
    label: { type: String, default: '' },
    selectedCause: {
      type: Object as () =>
        | import('@/interfaces/organizationgroup/donationCause').DonationCause
        | null,
      default: null,
    },
  },
  emits: ['search', 'clear'],
  setup(props, { emit }) {
    const { t } = useLabel();

    const query = ref('');
    const timerId = ref(0);

    const displayValue = computed(() => {
      return props.selectedCause ? props.selectedCause.name : query.value;
    });

    const fetchCauses = async (endpoint: string, params) => {
      try {
        const response = await get(endpoint, params);
        return response || {};
      } catch (error) {
        window.log?.error('[Guide] donationCauses.fetch — Failed to fetch donation causes', {
          message: guideErrorMessage(error),
          httpStatus: guideHttpStatus(error),
          endpoint: endpoint.split('?')[0],
        });
        return {
          paginatedResponse: { data: [], total: 0 },
        };
      }
    };

    const loadCauses = async (endpoint: string, extraParams = {}) => {
      const country = getUserCountry();
      const params = {
        onlyActive: true,
        country,
        ...extraParams,
      };
      return await fetchCauses(endpoint, params);
    };

    const performSearch = async (searchQuery: string) => {
      if (searchQuery.length < 2) return;

      const response = await loadCauses(
        `${ENDPOINTS.DONATION_CAUSES}/${ENDPOINTS.SEARCH}/${searchQuery}`
      );

      // Handle both direct array response (search API) and paginatedResponse structure (initial causes API)
      const results = Array.isArray(response) ? response : response?.paginatedResponse?.data || [];

      // Emit the search results
      emit('search', results, searchQuery);
    };

    const handleSearchInput = (value: string | Event) => {
      // Handle both string values and InputEvent objects
      const inputValue = typeof value === 'string' ? value : (value as any)?.target?.value || '';
      query.value = inputValue;

      // Clear previous timer
      clearTimeout(timerId.value);

      // Debounce the search
      timerId.value = window.setTimeout(() => {
        if (inputValue && inputValue.length >= 2) {
          performSearch(inputValue);
        } else if (inputValue.length <= 2) {
          // Clear search results when query is too short
          emit('clear');
        }
      }, 500);
    };

    const clearSearch = () => {
      query.value = '';
      emit('clear');
    };

    const clearSelectedCause = () => {
      emit('clearSelectedCause');
    };

    return {
      query,
      displayValue,
      handleSearchInput,
      clearSearch,
      clearSelectedCause,
      t,
    };
  },
});
</script>

<style scoped>
.search-input-container {
  width: 100%;
}

:deep(.v-input__control) {
  border-radius: 25px !important;
}
</style>
